import { Chip } from '@mui/material';
import type { SubscriptionStatus } from '@prospero/shared';
import { useTranslation } from 'react-i18next';
import { resolveManagedChipLabel } from '../pages/consultant/utils/client-format';
import { getSubscriptionStatusChipColor } from '../pages/consultant/utils/subscription-status-color';

interface TenantStatusChipProps {
  subscriptionStatus: SubscriptionStatus;
  isManaged: boolean;
  isManagedByMe: boolean;
  managedBy: { displayName: string } | null;
}

/**
 * Chip di stato accanto al nome di un'azienda nel selettore in alto: dice a
 * colpo d'occhio se il cliente è attivo, in prova, scaduto oppure è un'azienda
 * gestita da un consulente ("Gestita da te" o "Gestita da {nome}"). Un'azienda
 * gestita non ha un abbonamento proprio, quindi il suo stato ("ACTIVE"
 * tecnico) non va mostrato.
 */
export function TenantStatusChip({
  subscriptionStatus,
  isManaged,
  isManagedByMe,
  managedBy,
}: TenantStatusChipProps) {
  const { t } = useTranslation();

  function resolveLabel(): string {
    if (isManaged) {
      return resolveManagedChipLabel({ isManagedByMe, managedBy }, t);
    }
    // Short label: the menu row is narrow and "Periodo di prova" would squeeze the name.
    if (subscriptionStatus === 'TRIALING') {
      return t('consultant.trialChip');
    }
    return t(`consultant.clients.status.${subscriptionStatus}`, {
      defaultValue: subscriptionStatus,
    });
  }

  const color = isManaged ? 'info' : getSubscriptionStatusChipColor(subscriptionStatus);

  return (
    <Chip
      label={resolveLabel()}
      size="small"
      color={color}
      variant="outlined"
      sx={{
        flexShrink: 0,
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        color: (theme) => theme.palette[color].dark,
      }}
    />
  );
}
